import React, { useState } from 'react';
import AppIcon from '../ui/AppIcon';
import { FaChevronDown, FaChevronUp, FaQuestionCircle, FaBook, FaCreditCard, FaMobile, FaEnvelope } from 'react-icons/fa';

const HelpFAQ = () => {
  const [openSections, setOpenSections] = useState(new Set([0])); // First section open by default

  const toggleSection = (index) => {
    const newOpenSections = new Set(openSections);
    if (newOpenSections.has(index)) {
      newOpenSections.delete(index);
    } else {
      newOpenSections.add(index);
    }
    setOpenSections(newOpenSections);
  };

  const faqSections = [
    {
      category: "Getting Started",
      icon: <FaBook />,
      questions: [
        {
          question: "How do I create an account?",
          answer: "Click the 'Sign Up' button in the top right corner and enter your email address and password. You'll receive a confirmation email to verify your account."
        },
        {
          question: "What's included in the free access?",
          answer: "Free access includes chapter previews, descriptions, and basic mindfulness concepts. You can explore our content library and get a taste of our teaching methodology."
        },
        {
          question: "How do I start my mindfulness journey?",
          answer: "After creating an account, visit the Chapters page and start with the introductory content. We recommend beginning with Chapter 1 and progressing sequentially for the best learning experience."
        }
      ]
    },
    {
      category: "Subscriptions & Billing",
      icon: <FaCreditCard />,
      questions: [
        {
          question: "What are the different subscription options?",
          answer: "We offer three options: Monthly Premium ($9.99/month), Yearly Premium ($99.99/year - save $19.89), and One-time Purchase ($49.99 for lifetime access to premium content)."
        },
        {
          question: "Can I cancel my subscription anytime?",
          answer: "Yes, you can cancel your subscription at any time from your account settings. You'll retain access until the end of your current billing period."
        },
        {
          question: "What's the difference between paid and premium content?",
          answer: "Paid content includes full chapter materials and advanced concepts. Premium content adds interactive exercises, answer explanations, and progress tracking features."
        },
        {
          question: "Do you offer refunds?",
          answer: "Yes, we offer a 30-day money-back guarantee for all purchases. Contact our support team if you're not satisfied with your experience."
        }
      ]
    },
    {
      category: "Platform Features",
      icon: <FaQuestionCircle />,
      questions: [
        {
          question: "How do interactive exercises work?",
          answer: "Interactive exercises include multiple-choice questions, reflection prompts, and practical activities. Premium subscribers receive immediate feedback and detailed explanations for all answers."
        },
        {
          question: "Can I track my progress?",
          answer: "Yes, premium subscribers have access to detailed progress tracking, including completed chapters, exercise scores, and personal growth metrics."
        },
        {
          question: "Is my data secure and private?",
          answer: "Absolutely. We use enterprise-grade security with Firebase and encrypt all data. We never share your personal information with third parties. See our Privacy Policy for details."
        },
        {
          question: "Can I access content offline?",
          answer: "Currently, our platform requires an internet connection. We're working on offline capabilities for future releases."
        }
      ]
    },
    {
      category: "Mobile App",
      icon: <FaMobile />,
      questions: [
        {
          question: "Is there a mobile app available?",
          answer: "Yes! Our mobile app is available for both iOS and Android devices. You can use the same account credentials across all platforms."
        },
        {
          question: "Are features the same between web and mobile?",
          answer: "Yes, both platforms offer the same content and features. Your progress syncs automatically between devices."
        },
        {
          question: "How do I download the mobile app?",
          answer: "The mobile app is currently in development. We'll announce availability through our platform and email notifications."
        }
      ]
    },
    {
      category: "Technical Support",
      icon: <FaQuestionCircle />,
      questions: [
        {
          question: "I'm having trouble logging in. What should I do?",
          answer: "First, check that you're using the correct email and password. If you've forgotten your password, use the 'Forgot Password' link. For persistent issues, contact our support team."
        },
        {
          question: "The content isn't loading properly. How can I fix this?",
          answer: "Try refreshing the page, clearing your browser cache, or checking your internet connection. If problems persist, please contact support with details about your browser and device."
        },
        {
          question: "Can I use the platform on multiple devices?",
          answer: "Yes, you can access your account from any device with internet access. Your progress and preferences sync automatically."
        },
        {
          question: "What browsers are supported?",
          answer: "Our platform works best on modern browsers including Chrome, Firefox, Safari, and Edge. We recommend keeping your browser updated for the best experience."
        }
      ]
    }
  ];

  return (
    <div className="container">
      <div className="help-page">
        <div className="help-header">
          <AppIcon size={60} />
          <h1>Help & FAQ</h1>
          <p>Find answers to common questions about Nurturing Inner Love</p>
        </div>

        <div className="faq-container">
          {faqSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="faq-section">
              <div className="faq-category-header">
                {section.icon}
                <h2>{section.category}</h2>
              </div>
              
              <div className="faq-list">
                {section.questions.map((faq, questionIndex) => {
                  const globalIndex = sectionIndex * 100 + questionIndex;
                  const isOpen = openSections.has(globalIndex);
                  
                  return (
                    <div key={questionIndex} className="faq-item">
                      <button
                        className="faq-question"
                        onClick={() => toggleSection(globalIndex)}
                      >
                        <span>{faq.question}</span>
                        {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                      
                      {isOpen && (
                        <div className="faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="help-footer">
          <div className="help-cta">
            <h2>Still need help?</h2>
            <p>Can't find what you're looking for? Our support team is here to help.</p>
            <div className="help-actions">
              <a href="/contact" className="btn btn-primary">
                <FaEnvelope />
                Contact Support
              </a>
              <a href="mailto:support@nurturinginnerlove.com" className="btn btn-outline">
                Email Us Directly
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpFAQ;
